/**
 * Algolia Cart Attribution
 *
 * Passes Algolia search attribution data through cart line item properties
 * so the Web Pixel can access it for conversion tracking.
 *
 * Flow:
 * 1. When user clicks product from search results → store queryID/objectID
 * 2. When user adds to cart → inject as line item properties
 * 3. Web Pixel reads from checkout.lineItems[].customAttributes
 */

(function() {
  'use strict';

  const STORAGE_KEY = '_algolia_attribution';
  const DEBUG = window.algoliaConfig?.debug || false;
  const VERBOSE = true; // Temporary: verbose conversion debug logging

  // Store safe reference to fetch before any potential tampering
  const safeFetch = window.fetch.bind(window);

  function log(...args) {
    if (DEBUG || VERBOSE) {
      console.log('%c[Algolia Cart Attribution]', 'color: #5468ff; font-weight: bold', ...args);
    }
  }

  /**
   * Convert Algolia helper state filters to a compact string
   * Uses shared utility from AlgoliaShared
   */
  function getFiltersString(state) {
    return window.AlgoliaShared?.getFiltersString(state) || '';
  }

  /**
   * Store attribution data when user clicks a product from search
   * Called by algolia-analytics.js and algolia-collection-page.js
   */
  function storeAttribution(data) {
    if (!data.queryID || !data.objectID) {
      log('Missing queryID or objectID, not storing');
      return;
    }

    // Sanitize all input values to prevent injection attacks
    const sanitize = window.AlgoliaShared?.sanitizeValue || ((v) => String(v || '').substring(0, 500));

    const attribution = {
      queryID: sanitize(data.queryID, 100),
      objectID: sanitize(data.objectID, 100),
      handle: sanitize(data.handle || '', 200),
      indexName: sanitize(data.indexName || window.algoliaConfig?.indexName || 'shopify_products', 100),
      position: Math.max(1, parseInt(data.position, 10) || 1),
      query: sanitize(data.query || '', 200),
      filters: sanitize(data.filters || '', 500),
      price: data.price ? parseFloat(data.price) || 0 : undefined,
      quantity: data.quantity ? parseInt(data.quantity, 10) || 1 : undefined,
      discount: data.discount ? parseFloat(data.discount) || 0 : undefined,
      timestamp: Date.now()
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
      log('Stored attribution:', attribution);
    } catch (e) {
      log('Failed to store attribution:', e);
    }
  }

  /**
   * Get stored attribution data
   */
  function getAttribution() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        // Only use if within timeout window
        const timeoutMinutes = window.AlgoliaShared?.ATTRIBUTION_TIMEOUT_MINUTES || 30;
        if (Date.now() - data.timestamp >= timeoutMinutes * 60 * 1000) {
          log('Attribution expired, clearing');
          clearAttribution();
          return null;
        }
        // On a PDP, only credit the search click that led to this product
        const pageHandle = window.algoliaConfig?.pageContext?.productHandle;
        if (data.handle && pageHandle && data.handle !== pageHandle) {
          log('Attribution is for another product, not using it');
          return null;
        }
        return data;
      }
    } catch (e) {
      log('Failed to get attribution:', e);
    }
    return null;
  }

  /**
   * Clear stored attribution after use
   */
  function clearAttribution() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      log('Cleared attribution');
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Hook into product form submissions to inject Algolia properties
   */
  function setupFormInterception() {
    // Intercept all product form submissions
    document.addEventListener('submit', function(event) {
      const form = event.target;

      // Check if this is a product add-to-cart form
      if (!form.matches('form[is="product-form"], form[action*="/cart/add"]')) {
        return;
      }

      const attribution = getAttribution();
      if (!attribution) {
        log('No attribution data for this add-to-cart');
        return;
      }

      // For forms using FormData (AJAX submissions), we need to intercept differently
      // The ProductForm class creates FormData after this event, so we'll use a different approach
      log('Form submission detected, attribution will be injected via fetch intercept');
    }, true);

    // Intercept fetch calls to /cart/add
    interceptFetch();
  }

  /**
   * Intercept fetch calls to inject Algolia properties into cart add requests
   * Uses safeFetch reference stored at module load to prevent tampering
   */
  function interceptFetch() {
    const interceptedFetch = function(url, options) {
      // Check if this is a cart add request
      const urlString = typeof url === 'string' ? url : url.toString();

      if (urlString.includes('/cart/add') && options?.body) {
        log('>>> /cart/add INTERCEPTED. Body type:', options.body?.constructor?.name || typeof options.body);
        const attribution = getAttribution();

        if (attribution) {
          log('>>> ATTRIBUTION FOUND for /cart/add:', {
            queryID: attribution.queryID,
            objectID: attribution.objectID,
            indexName: attribution.indexName,
            age: Math.round((Date.now() - attribution.timestamp) / 1000) + 's'
          });
          try {
            // Handle FormData body
            if (options.body instanceof FormData) {
              options.body.append('properties[_algolia_queryID]', attribution.queryID);
              options.body.append('properties[_algolia_objectID]', attribution.objectID);
              options.body.append('properties[_algolia_index]', attribution.indexName);
              options.body.append('properties[_algolia_position]', String(attribution.position || 1));
              if (attribution.query) {
                options.body.append('properties[_algolia_query]', attribution.query);
              }
              if (attribution.filters) {
                options.body.append('properties[_algolia_filters]', attribution.filters);
              }
              log('>>> INJECTED into FormData. Entries:',
                [...options.body.entries()].filter(([k]) => k.startsWith('properties[')).map(([k, v]) => `${k}=${v}`));
            }
            // Handle JSON body
            else if (typeof options.body === 'string') {
              try {
                const body = JSON.parse(options.body);
                body.properties = body.properties || {};
                body.properties['_algolia_queryID'] = attribution.queryID;
                body.properties['_algolia_objectID'] = attribution.objectID;
                body.properties['_algolia_index'] = attribution.indexName;
                body.properties['_algolia_position'] = String(attribution.position || 1);
                if (attribution.query) {
                  body.properties['_algolia_query'] = attribution.query;
                }
                if (attribution.filters) {
                  body.properties['_algolia_filters'] = attribution.filters;
                }
                options.body = JSON.stringify(body);
                log('>>> INJECTED into JSON body. Properties:', body.properties);
              } catch (e) {
                log('>>> Body is not JSON, skipping injection. Body type:', typeof options.body, 'Preview:', String(options.body).substring(0, 100));
              }
            } else {
              log('>>> UNHANDLED body type:', options.body?.constructor?.name || typeof options.body);
            }
          } catch (e) {
            log('>>> FAILED to inject attribution:', e);
          }
        } else {
          log('>>> NO attribution in sessionStorage for /cart/add request');
        }
      }

      return safeFetch.apply(this, arguments);
    };

    try {
      window.fetch = interceptedFetch;
    } catch (e) {
      log('window.fetch is read-only, skipping fetch interception:', e);
      return;
    }

    log('Fetch interception set up');
  }

  /**
   * Listen for successful cart additions to verify injection and clear attribution
   */
  function setupCartListeners() {
    // After ATC succeeds, verify that properties made it into the cart
    document.addEventListener('product-ajax:added', function() {
      log('>>> product-ajax:added fired. Verifying cart line item properties...');
      // Fetch /cart.js to verify properties were injected
      safeFetch('/cart.js').then(r => r.json()).then(cart => {
        const lastItem = cart.items?.[0];
        if (lastItem) {
          log('>>> Cart verification - last item properties:', lastItem.properties || 'NONE');
          if (lastItem.properties?._algolia_queryID) {
            log('>>> SUCCESS: _algolia_queryID is in cart line item properties');
          } else {
            log('>>> WARNING: _algolia_queryID NOT found in cart line item. Properties:', JSON.stringify(lastItem.properties));
          }
        }
      }).catch(e => log('>>> Cart verification failed:', e));
      clearAttribution();
    });

    // Also listen for cart:item-added (used by algolia-analytics.js)
    document.addEventListener('cart:item-added', function() {
      clearAttribution();
    });
  }

  /**
   * Store attribution when clicking product from search results
   * This is called from algolia-collection-page.js and algolia-analytics.js
   */
  function setupClickListener() {
    // Listen for Algolia product clicks that include search context
    document.addEventListener('click', function(event) {
      const productLink = event.target.closest('a[href*="/products/"]');
      if (!productLink) return;

      // Check if this click is from Algolia search results
      const productCard = productLink.closest('.product-card, .predictive-search__result-link, [data-algolia-hit]');
      if (!productCard) return;

      // Get queryID from various sources
      const adapter = window.algoliaCollectionPage || window.algoliaSearchPage || window.algoliaAdapter;
      const queryID = adapter?.lastQueryID || adapter?.search?.helper?.lastResults?.queryID;

      if (!queryID) {
        log('>>> CLICK on product link but NO queryID available. Adapter state:', {
          hasCollectionPage: !!window.algoliaCollectionPage,
          hasSearchPage: !!window.algoliaSearchPage,
          hasAdapter: !!window.algoliaAdapter,
          collectionQueryID: window.algoliaCollectionPage?.lastQueryID || 'none',
          searchQueryID: window.algoliaSearchPage?.lastQueryID || 'none'
        });
        return;
      }

      // Extract product info
      const href = productLink.getAttribute('href');
      const productHandle = href.match(/\/products\/([^/?]+)/)?.[1];

      // Get objectID and position from hits
      const hits = adapter?.lastHits || adapter?.search?.helper?.lastResults?.hits || [];
      const hitIndex = hits.findIndex(h => h.handle === productHandle || h.objectID?.includes(productHandle));
      const hit = hitIndex >= 0 ? hits[hitIndex] : null;
      const objectID = hit?.objectID;
      if (!objectID) {
        log('Product not found in hits, skipping attribution:', productHandle);
        return;
      }
      const position = hitIndex >= 0 ? hitIndex + 1 : 1;

      // Get search query and filters from helper state
      const helperState = adapter?.search?.helper?.state;
      const query = helperState?.query || '';
      const filters = getFiltersString(helperState);

      log('>>> CLICK tracked. Storing attribution:', { queryID, objectID, position, query, productHandle });
      // Store for later use when adding to cart
      storeAttribution({
        queryID: queryID,
        objectID: objectID,
        handle: productHandle,
        indexName: adapter?.getCurrentIndexName?.() || adapter?.search?.helper?.state?.index || window.algoliaConfig?.indexName,
        position: position,
        query: query,
        filters: filters
      });
    }, true);
  }

  // Initialize
  function init() {
    if (typeof window.algoliaConfig === 'undefined') {
      log('Algolia not configured, skipping cart attribution setup');
      return;
    }

    setupFormInterception();
    setupCartListeners();
    setupClickListener();

    log('Cart attribution initialized');
  }

  // Expose for external use (e.g., from algolia-collection-page.js)
  window.AlgoliaCartAttribution = {
    store: storeAttribution,
    get: getAttribution,
    clear: clearAttribution
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
